import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useLocale } from "../context/Locale";

function money(value) {
  return `S/ ${Number(value || 0).toFixed(2)}`;
}

function addonCategoryIcon(name) {
  const value = String(name || "").toLocaleLowerCase("es");
  if (/(salsa|crema|ají|aji|mayonesa|ketchup|mostaza)/.test(value)) return "🧴";
  if (/(papa|frita|guarnición|guarnicion)/.test(value)) return "🍟";
  if (/(picante|presentación|presentacion|sabor)/.test(value)) return "🌶️";
  if (/(bebida|gaseosa|jugo)/.test(value)) return "🥤";
  return "➕";
}

export default function ProductCustomizer({ itemId, restaurant, onClose, onConfirm }) {
  const { t } = useLocale();
  const [item, setItem] = useState(null);
  const [selected, setSelected] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api("/get-single-item", {
      method: "POST",
      body: JSON.stringify({ id: itemId }),
    })
      .then((data) => {
        if (active) setItem(data);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [itemId]);

  const chosenAddons = useMemo(() => {
    if (!item) return [];
    return item.addon_categories.flatMap((category) =>
      (selected[category.id] || []).map((addonId) => {
        const addon = category.addons.find((candidate) => Number(candidate.id) === Number(addonId));
        return addon
          ? {
              addon_category_name: category.name,
              addon_id: addon.id,
              addon_name: addon.name,
              price: Number(addon.price || 0),
            }
          : null;
      }).filter(Boolean),
    );
  }, [item, selected]);

  const unitPrice = Number(item?.price || 0) +
    chosenAddons.reduce((sum, addon) => sum + Number(addon.price || 0), 0);
  const addonPrice = chosenAddons.reduce(
    (sum, addon) => sum + Number(addon.price || 0),
    0,
  );

  function toggleAddon(category, addon) {
    setSelected((current) => {
      const values = current[category.id] || [];
      const exists = values.some((value) => Number(value) === Number(addon.id));
      if (exists) {
        return { ...current, [category.id]: values.filter((value) => Number(value) !== Number(addon.id)) };
      }
      if (String(category.type).toUpperCase() === "SINGLE") {
        return { ...current, [category.id]: [addon.id] };
      }
      const limit = Number(category.addon_limit || 0);
      if (limit > 0 && values.length >= limit) return current;
      return { ...current, [category.id]: [...values, addon.id] };
    });
  }

  function confirm() {
    onConfirm(item, restaurant, chosenAddons, quantity);
    onClose();
  }

  return (
    <div className="product-modal" role="presentation" onMouseDown={onClose}>
      <section
        className="product-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-sheet-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="product-sheet__header">
          <button type="button" onClick={onClose} aria-label={t("back")}>‹</button>
          <h1>{t("customizeOrder")}</h1>
          <button type="button" onClick={onClose} aria-label={t("close")}>×</button>
        </header>
        {loading && <div className="product-sheet__loading">{t("loadingProduct")}</div>}
        {error && <div className="checkout-error">{error}</div>}
        {item && (
          <>
            <section className="product-sheet__product">
              <div className="product-sheet__image">
                {item.image ? <img src={item.image} alt={item.name} /> : <span>🍽️</span>}
              </div>
              <div className="product-sheet__intro">
                <h2 id="product-sheet-title">{item.name}</h2>
                <p>{t("chooseOptions")}</p>
                <b>{money(item.price)}</b>
              </div>
            </section>

            <div className="addon-groups">
              {item.addon_categories.map((category) => {
                const chosen = selected[category.id] || [];
                const limit = Number(category.addon_limit || 0);
                return (
                  <section className="addon-group" key={category.id}>
                    <header className="addon-group__header">
                      <span aria-hidden="true">{addonCategoryIcon(category.name)}</span>
                      <div>
                        <h3>{category.name}</h3>
                        <small>
                          {String(category.type).toUpperCase() === "SINGLE"
                            ? t("chooseOneOption")
                            : limit > 0 ? t("chooseUpTo").replace("{count}", limit) : t("optional")}
                        </small>
                      </div>
                      {limit > 0 && (
                        <b>{chosen.length} de {limit}</b>
                      )}
                    </header>
                    {category.addons.map((addon) => {
                      const checked = chosen.some((value) => Number(value) === Number(addon.id));
                      return (
                        <label className={checked ? "is-selected" : ""} key={addon.id}>
                          <span className="addon-option__copy">
                            <strong>{addon.name}</strong>
                            <small>{Number(addon.price) > 0 ? t("additional") : t("noCost")}</small>
                          </span>
                          <b>{Number(addon.price) > 0 ? `+ ${money(addon.price)}` : ""}</b>
                          <input
                            type={String(category.type).toUpperCase() === "SINGLE" ? "radio" : "checkbox"}
                            name={`addon-${category.id}`}
                            checked={checked}
                            onChange={() => toggleAddon(category, addon)}
                          />
                        </label>
                      );
                    })}
                  </section>
                );
              })}
            </div>

            <footer className="product-sheet__footer">
              <div className="product-sheet__totals">
                <span>{t("additionals")}</span>
                <b>{money(addonPrice * quantity)}</b>
              </div>
              <div className="quantity-control">
                <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))}>−</button>
                <strong>{quantity}</strong>
                <button type="button" onClick={() => setQuantity((value) => value + 1)}>+</button>
              </div>
              <button className="product-sheet__continue" type="button" onClick={confirm} aria-label={`${t("continueAndAdd")} ${money(unitPrice * quantity)}`}>
                <span>{t("continue")}</span>
                <strong>{money(unitPrice * quantity)}</strong>
                <i aria-hidden="true">→</i>
              </button>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}
